import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, TrendingUp, Shield, Target, PieChart } from 'lucide-react';

interface Question {
  id: number;
  question: string;
  options: {
    text: string;
    value: number;
    description: string;
  }[];
}

interface SimulateurProfilRisqueProps {
  onNavigateBack: () => void;
  onComplete: (results: any) => void;
}

const SimulateurProfilRisque: React.FC<SimulateurProfilRisqueProps> = ({ onNavigateBack, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [showResults, setShowResults] = useState(false);

  const questions: Question[] = [
    {
      id: 1,
      question: "Quel est votre objectif d'investissement principal ?",
      options: [
        {
          text: "Constituer une réserve de sécurité",
          value: 1,
          description: "Horizon 1-3 ans, besoin de liquidité"
        },
        {
          text: "Financer un projet spécifique",
          value: 2,
          description: "Achat mobilier, études des enfants, hadj, mariage - horizon 3-5 ans"
        },
        {
          text: "Préparer un projet d'acquisition",
          value: 3,
          description: "Immobilier, horizon long terme, plus de 5 ans"
        },
        {
          text: "Anticiper la retraite et transmettre",
          value: 4,
          description: "Horizon très long terme, transmission patrimoniale"
        }
      ]
    },
    {
      id: 2,
      question: "Quelle est votre expérience en matière d'investissement ?",
      options: [
        {
          text: "Débutant",
          value: 1,
          description: "Je n'ai jamais investi sur les marchés financiers"
        },
        {
          text: "Occasionnel",
          value: 2,
          description: "J'investis de temps en temps, plutôt sur des produits sécurisés"
        },
        {
          text: "Régulier",
          value: 3,
          description: "J'investis plusieurs fois par an, je connais les produits de base"
        },
        {
          text: "Expérimenté",
          value: 4,
          description: "J'investis fréquemment, je maîtrise différents types de placements"
        }
      ]
    },
    {
      id: 3,
      question: "Comment réagiriez-vous si votre portefeuille perdait 15% de sa valeur ?",
      options: [
        {
          text: "Je vendrais immédiatement",
          value: 1,
          description: "Pour limiter les pertes"
        },
        {
          text: "Je serais très inquiet",
          value: 2,
          description: "Mais j'attendrais quelques mois avant de décider"
        },
        {
          text: "Je resterais patient",
          value: 3,
          description: "En gardant ma stratégie à long terme"
        },
        {
          text: "J'en profiterais pour investir davantage",
          value: 4,
          description: "À des prix plus attractifs"
        }
      ]
    }
  ];

  const calculateRiskProfile = () => {
    const totalScore = Object.values(answers).reduce((sum, score) => sum + score, 0);
    const averageScore = totalScore / questions.length;

    if (averageScore <= 1.5) {
      return {
        profile: 'Protecteur',
        color: 'emerald',
        icon: Shield,
        description: 'Vous privilégiez la sécurité et la préservation de votre capital.',
        allocation: 'Sukuk (60%) + SCPI (40%)',
        expectedReturn: '3.8% - 4.2%',
        risk: 'Faible'
      };
    } else if (averageScore <= 2.5) {
      return {
        profile: 'Équilibré',
        color: 'blue',
        icon: Target,
        description: 'Vous recherchez un équilibre entre sécurité et performance.',
        allocation: 'SCPI (50%) + GFI (30%) + Sukuk (20%)',
        expectedReturn: '4.5% - 5.8%',
        risk: 'Modéré'
      };
    } else {
      return {
        profile: 'Dynamique',
        color: 'purple',
        icon: TrendingUp,
        description: 'Vous acceptez plus de risques pour un potentiel de rendement élevé.',
        allocation: 'GFI (60%) + SCPI (30%) + Sukuk (10%)',
        expectedReturn: '5.2% - 6.5%',
        risk: 'Élevé'
      };
    }
  };

  const handleAnswerSelect = (value: number) => {
    setAnswers({ ...answers, [questions[currentQuestion].id]: value });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const isCurrentAnswered = answers[questions[currentQuestion].id] !== undefined;
  const currentAnswer = answers[questions[currentQuestion].id];

  if (showResults) {
    const result = calculateRiskProfile();
    const IconComponent = result.icon;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={onNavigateBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Retour</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">Résultats de votre profil</h1>
              <div></div>
            </div>
          </div>
        </div>

        {/* Results Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <div className={`w-24 h-24 bg-${result.color}-100 rounded-full flex items-center justify-center mx-auto mb-6`}>
              <IconComponent className={`w-12 h-12 text-${result.color}-600`} />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Profil <span className={`text-${result.color}-600`}>{result.profile}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {result.description}
            </p>
          </div>

          {/* Strategy Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Votre stratégie d'investissement recommandée
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <PieChart className="w-8 h-8 text-teal-600 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 mb-2">Allocation</h4>
                <p className="text-sm text-gray-600">{result.allocation}</p>
              </div>
              
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 mb-2">Rendement attendu</h4>
                <p className="text-sm text-gray-600">{result.expectedReturn}</p>
              </div>
              
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <Shield className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 mb-2">Niveau de risque</h4>
                <p className="text-sm text-gray-600">{result.risk}</p>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => onComplete(result)}
                className="bg-gradient-to-r from-teal-600 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-teal-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-3 mx-auto shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Voir les produits recommandés</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={onNavigateBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Analyse de profil de risque</h1>
            <div className="text-sm text-gray-500">
              {currentQuestion + 1} / {questions.length}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-teal-500 to-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {questions[currentQuestion].question}
          </h2>

          <div className="space-y-4 mb-8">
            {questions[currentQuestion].options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(option.value)}
                className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                  currentAnswer === option.value
                    ? 'border-teal-500 bg-teal-50 shadow-lg'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-1 ${
                    currentAnswer === option.value
                      ? 'border-teal-500 bg-teal-500'
                      : 'border-gray-300'
                  }`}>
                    {currentAnswer === option.value && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">{option.text}</h3>
                    <p className="text-sm text-gray-600">{option.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                currentQuestion === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Précédent</span>
            </button>

            <button
              onClick={handleNext}
              disabled={!isCurrentAnswered}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                isCurrentAnswered
                  ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white hover:from-teal-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <span>{currentQuestion === questions.length - 1 ? 'Voir mes résultats' : 'Suivant'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulateurProfilRisque;